/**
 * Installations admin API client — mirrors backend
 * /api/v1/admin/installations/* routes (installations.routes.ts).
 *
 * Response shape: focus endpoints return `{ success, data }`; DELETE returns
 * `{ ok: true }`. These helpers unwrap `.data.data` so callers get the row.
 */
import { api } from '@/utils/api';

export const INSTALLATION_CATEGORIES = [
  'SS_WATER_TANK',
  'DOMESTIC_HEAT_PUMP',
  'COMMERCIAL_HEAT_PUMP',
  'POOL_HEAT_PUMP',
  'SOLAR_WATER_HEATER',
  'HYBRID_HEAT_PUMP',
  'SOLAR_SPACE_HEATING',
  'SS_PLUMBING',
] as const;

export type InstallationCategory = (typeof INSTALLATION_CATEGORIES)[number];

export interface FocusBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Installation {
  id: string;
  category: string;
  projectName: string;
  customer?: string | null;
  state: string | null;
  city: string | null;
  region?: string | null;
  year: number | null;
  spec?: string | null;
  isNationalBest?: boolean;
  sortOrder?: number;
  publicUrl: string;
  /**
   * Stable, cacheable, HMAC-signed thumbnail URL
   * (`/api/v1/admin/installations/:id/thumb?sig=...`). ~30 KB WebP. Used by the
   * admin grid for light lazy-loading; the full `publicUrl` (presigned) stays
   * for click/edit/crop.
   */
  thumbUrl: string;
  focusBox: FocusBox | null;
  focusSource: 'ai' | 'manual' | null;
  focusConfidence: number | null;
  focusReviewed: boolean;
  focusUpdatedAt: string | null;
}

export interface UpdateInstallationBody {
  projectName?: string;
  customer?: string | null;
  city?: string | null;
  state?: string | null;
  region?: string | null;
  category?: InstallationCategory;
  isNationalBest?: boolean;
  sortOrder?: number;
  year?: number | null;
  spec?: string | null;
}

export interface UploadInstallationBody {
  file: File;
  category: InstallationCategory;
  projectName: string;
  customer?: string;
  city?: string;
  state?: string;
  region?: string;
  year?: number;
  isNationalBest?: boolean;
}

// ── List ──────────────────────────────────────────────────────────────────────

export async function listInstallations(params: {
  category?: string;
} = {}): Promise<Installation[]> {
  const query: Record<string, string> = {};
  if (params.category) query.category = params.category;
  const res = await api.get('/admin/installations', { params: query });
  return (res.data?.data ?? []) as Installation[];
}

// ── Update metadata ─────────────────────────────────────────────────────────

export async function updateInstallation(
  id: string,
  body: UpdateInstallationBody,
): Promise<Installation> {
  const res = await api.patch(`/admin/installations/${id}`, body);
  return (res.data?.data ?? res.data) as Installation;
}

// ── Delete ──────────────────────────────────────────────────────────────────

export async function deleteInstallation(id: string): Promise<void> {
  await api.delete(`/admin/installations/${id}`);
}

// ── Upload (multipart) ──────────────────────────────────────────────────────

export async function uploadInstallation(
  body: UploadInstallationBody,
): Promise<Installation> {
  const form = new FormData();
  form.append('file', body.file);
  form.append('category', body.category);
  form.append('projectName', body.projectName);
  if (body.customer) form.append('customer', body.customer);
  if (body.city) form.append('city', body.city);
  if (body.state) form.append('state', body.state);
  if (body.region) form.append('region', body.region);
  if (body.year != null) form.append('year', String(body.year));
  if (body.isNationalBest != null)
    form.append('isNationalBest', String(body.isNationalBest));
  const res = await api.post('/admin/installations', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return (res.data?.data ?? res.data) as Installation;
}
